import React from 'react';
import { Tooltip, Typography } from 'antd';
import type { SuggestedField } from '../../types/expense';

const { Text } = Typography;

interface Props<T> {
  field?: SuggestedField<T> | null;
  /** El usuario ya editó este campo: la marca desaparece. */
  edited: boolean;
  /** Cómo mostrar el valor descartado de un conflicto. */
  format?: (value: T) => string;
}

/**
 * Marca de dónde salió un campo precargado y cuánto hay que desconfiar de él.
 *
 * <b>Sin esto la feature es un riesgo, no una ayuda.</b> Si todos los campos se ven
 * iguales, el empleado no sabe cuál revisar y la revisión se vuelve nominal — y el modo de
 * falla caro de esta feature no es el comprobante ilegible (eso es un no-evento) sino el
 * monto leído mal y confirmado sin mirar, que entra a producción con apariencia de
 * verificado.
 *
 * La marca se quita al editar: un aviso que siempre está es un aviso que nadie lee.
 */
function SuggestedFieldMark<T>({ field, edited, format }: Props<T>) {
  if (!field || edited) {
    return null;
  }

  const show = format ?? ((value: T) => String(value));

  if (field.confidence === 'Conflict' && field.conflict) {
    return (
      <Tooltip
        title={`El QR dice ${show(field.value)} pero en el comprobante se lee ${show(field.conflict.value)}. Verifica cuál es.`}
      >
        <Text style={{ color: 'var(--color-error)' }}>
          🔴 El QR y el texto no coinciden — verifica
        </Text>
      </Tooltip>
    );
  }

  if (field.source === 'Qr') {
    return (
      <Tooltip title="Parsear el QR prueba que el texto está bien formado, no que la factura exista o esté vigente.">
        <Text type="secondary">🧾 Del QR (sin verificar con el SAT)</Text>
      </Tooltip>
    );
  }

  if (field.confidence === 'Low') {
    return (
      <Text style={{ color: 'var(--color-warning)' }}>⚠️ Leído con dudas — verifica</Text>
    );
  }

  return <Text type="secondary">✨ Leído del comprobante</Text>;
}

export default SuggestedFieldMark;
